/**
 * The Finance board's read model (GET /finance/board), as
 * backend/src/services/finance/financeBoard.js returns it. No SSN is ever in
 * it; approval facts are present only when `approval_detail` is true.
 */
import api from '../../services/api';
import type { ApiResult } from './financeApi';
import type { BoardPage } from '../../components/board';
import { boardQueryString } from '../scheduling/boardApi';

export interface MondayMoney { value: number | null; source: 'MONDAY'; read_only: true }
export interface FinanceBoardRow {
  id: string;
  /** null: the board item names no resident (it cannot open the dossier). */
  resident: { id: string; name: string } | null;
  resident_accounts: number | null;
  status: { word: string; color: string | null; known: boolean } | null;
  group?: { id: string; title: string | null; source: 'MONDAY'; as_of: string | null } | null;
  reps: { monday_user_id: string; name: string | null; source: 'MONDAY' }[];
  facility: { id: string; name: string | null; source: 'MONDAY' | 'EMERGE' } | null;
  sale_value: MondayMoney;
  payments_received: MondayMoney;
  remaining: MondayMoney;
  overdue: MondayMoney;
  terms: { text: string; source: 'MONDAY'; read_only: true } | null;
  refund: { amount: number | null; on: string | null; source: 'MONDAY' } | null;
  reversal_date: { on: string; source: 'MONDAY' } | null;
  dcf_correction_date: { on: string; source: 'MONDAY' } | null;
  deceased_date: { on: string; source: 'MONDAY' } | null;
  invoice_created: { value: boolean; source: 'MONDAY' } | null;
  notes_count: number | null;
  /** `corrected`: amendments on this resident's handoffs (ruling #1, 2026-10-07) -- count = rows, last_on = latest day (Florida). */
  handoff: { finance_state: string | null; umed_obligation_open: boolean; kind?: 'UMED' | 'PRIVATE_PAY' | null; corrected?: { count: number; last_on: string | null } | null; source: 'EMERGE' } | null;
  /** Present only when the caller may read the approval facts. */
  /**
   * `proven` is the approval ACT (approved in eMerge); `stage` and
   * `finance_state` are the case's own, so a stage Monday set to approved is
   * named as Monday's claim (final verification 2026-10-06, P2-C).
   */
  approval?: { noca: string | null; flmmis: string | null; proven: boolean; approved_on: string | null; stage?: string | null; finance_state?: string | null; source: 'EMERGE' } | null;
  board_updated_at: string | null;
}
export interface FinanceFilterOption { id: string; name: string; count: number }
export interface FinanceBoardPage extends BoardPage<FinanceBoardRow> {
  filters: { rep: string | null; building: string | null; q: string | null };
  filter_options: { reps: FinanceFilterOption[]; buildings: FinanceFilterOption[] };
  approval_detail: boolean;
}
export interface FinanceBoardQuery { tab?: string; q?: string; rep?: string; building?: string; sort?: string | null; dir?: 'asc' | 'desc'; page?: number }

export const financeBoardApi = {
  board: async (p: FinanceBoardQuery): Promise<ApiResult<FinanceBoardPage>> =>
    (await api.get(`/finance/board${boardQueryString({ tab: p.tab, q: p.q, rep: p.rep, building: p.building, sort: p.sort, dir: p.sort ? p.dir : null, page: p.page })}`)) as ApiResult<FinanceBoardPage>,
};
